// Used in student-assignment.component.html and grade-assignments.component.html

// Angular
import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from "@angular/core";

// Services/Types/Helpers
import { AssetAssociationsHelper } from "#helpers/asset-associations.helper";
import { AssignmentStatusHelper } from "#helpers/assignment-status.helper";
import { DateFormatterHelper } from "#helpers/date-formatter.helper";
import { DurationFormat } from "#helpers/durationFormat.helper";
import { LanguageHelper } from "#helpers/language.helper";
import {
    AssetTypeHelper,
    PPAssignment as Assignment,
    CompositeTaskAssignment,
    Course,
    PPUser as User,
} from "cli-types";

// RXJS/Store
import * as coursesStore from "#core/teacher-classes/store";
import * as profileStore from "#login/store";
import { select, Store } from "@ngrx/store";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

// Components
import { AssignmentHelper } from "#helpers/assignment.helper";
import { LevelId, SubjectId } from "@carnegie-learning/common-types";
import { Asset, assetActivityTypeIds, AssetAssociation, assetTypeIds, Bundle } from "@carnegie-learning/cure-types";

@Component({
    selector: "view-composite-assignment",
    templateUrl: "./view-composite-assignment.component.html",
    styleUrls: ["./view-composite-assignment.component.scss"],
    standalone: false
})
export class ViewCompositeAssignmentComponent implements OnInit, OnDestroy {
    readonly assetTypeIds = assetTypeIds;
    readonly assetActivityTypeIds = assetActivityTypeIds;
    course: Course;
    userProfile: User;
    bundleStatus: string;
    activityInstructions = "Click row to view selected activity.";
    bundleWithEbook: boolean;
    compositeActivitiesName = {
        name: "Activities",
        fontSize: "14px",
    };

    @Input() assignment: CompositeTaskAssignment;
    @Input() currentCourseSubjectId: SubjectId;
    @Input() maxAttemptsUpdated: boolean;
    @Input() subjects: AssetAssociation[];

    @Output() resetBundleActivityStatus = new EventEmitter<number>();

    private ngUnsubscribe = new Subject();

    constructor(
        private assignmentHelper: AssignmentHelper,
        private assetTypeHelper: AssetTypeHelper,
        private assignmentStatusHelper: AssignmentStatusHelper,
        private dateFormatterHelper: DateFormatterHelper,
        private languageHelper: LanguageHelper,
        private durationFormat: DurationFormat,
        public profile_store: Store<profileStore.UserState>,
        public store_courses: Store<coursesStore.CoursesState>,
        private assetAssociationsHelper: AssetAssociationsHelper
    ) {}

    ngOnInit() {
        this.profile_store
            .pipe(select(profileStore.getUserProfile))
            .subscribe((userProfile) => {
                if (userProfile) {
                    this.userProfile = userProfile;
                }
            });

        this.store_courses
            .pipe(select(coursesStore.getCourseByRoute))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((course) => {
                if (!course) {
                    return;
                }
                this.course = course;
            });

        this.bundleStatus = this.getBundleStatus();

        this.bundleWithEbook =
            this.assignment.shortAssignableType === assetTypeIds.BUNDLE &&
            this.assignment.compositeTaskAssignments.some(
                (obj) => obj.assetTypeId === assetTypeIds.EBOOK_ACT
            );
        const assetTypeName = this.BundleTypeName(this.assignment);
        if (assetTypeName) {
            this.compositeActivitiesName.name = assetTypeName + " Activities";
        }
    }

    getIconPath(asset: Asset): string {
        if (asset) {
            if (asset.assetActivityTypeId) {
                return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
                    asset.assetActivityTypeId
                );
            } else {
                return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
                    asset.assetTypeId
                );
            }
        } else {
            return "";
        }
    }

    getDate(date: string) {
        return this.dateFormatterHelper.formatDateOneLine(date);
    }

    resetStatus(compositeTaskAssignmentID: number) {
        this.resetBundleActivityStatus.emit(compositeTaskAssignmentID);
    }

    getBundleStatus() {
        const isInProgress = this.assignment.compositeTaskAssignments.every(
            (activity) => {
                return activity.startedAt && activity.completedAt == null;
            }
        );
        if (isInProgress) {
            return "IN PROGRESS";
        }

        const isComplete = this.assignment.compositeTaskAssignments.every(
            (activity) => {
                return activity.startedAt && activity.completedAt;
            }
        );

        if (isComplete && this.assignment.status !== "evaluated") {
            return "SUBMITTED";
        } else if (isComplete && this.assignment.status === "evaluated") {
            return "EVALUATED";
        } else {
            return "---";
        }
    }

    isMastered() {
        if (this.assignment && this.assignment.compositeTaskAssignments) {
            let masteryMastered = true;
            let conceptBuilderMastered = true;
            this.assignment.compositeTaskAssignments.forEach((activity) => {
                if (
                    activity.asset?.assetActivityTypeId ===
                        this.assetActivityTypeIds.NON_MASTERY &&
                    activity.performanceEarned &&
                    activity.performanceEarned < 0.7
                ) {
                    conceptBuilderMastered = false;
                }
                if (
                    activity.asset?.assetActivityTypeId ===
                        this.assetActivityTypeIds.MASTERY &&
                    activity.performanceEarned &&
                    activity.performanceEarned < 0.95
                ) {
                    masteryMastered = false;
                }
            });
            return conceptBuilderMastered && masteryMastered;
        }
        return false;
    }

    getBundleLearningPoints() {
        let progressEarned = 0;
        const progressPossible = this.assignmentHelper.getAssetProgressPossible(
            <Bundle>this.assignment.asset
        );
        this.assignment.compositeTaskAssignments.forEach((activity) => {
            if (activity.progressEarned) {
                progressEarned += parseFloat(
                    activity.progressEarned.toFixed(1)
                ); // simply using activity.progressEarned incurs rounding errors
            }
        });
        return (
            (progressEarned ? progressEarned.toFixed(1) : "---") +
            "/" +
            progressPossible
        );
    }

    getBundleTotalTime() {
        let duration = 0;
        if (this.assignment && this.assignment.compositeTaskAssignments) {
            this.assignment.compositeTaskAssignments.forEach((activity) => {
                if (activity && activity.duration !== null) {
                    duration =
                        activity.duration > 0 && activity.duration < 60
                            ? duration + 60 // duration is measured in seconds, not minutes.
                            : duration +
                              Math.round(activity.duration / 60) * 60;
                }
            });
        }
        return duration ? this.durationFormat.formatDuration(duration) : "---";
    }

    ifMathProgram() {
        return this.languageHelper.ifMathLanguage(this.course.subjectId);
    }

    showResetButton(activity: Assignment | CompositeTaskAssignment) {
        if (this.course) {
            if (
                (this.isComposite() || this.isCompositeActivity()) &&
                activity.asset?.assetTypeId !== assetTypeIds.EBOOK_ACT
            ) {
                return true;
            } else if (
                (this.isComposite() || this.isCompositeActivity()) &&
                activity.asset?.assetTypeId === assetTypeIds.EBOOK_ACT
            ) {
                // If assignment has attempts left
                const maxAttempts = activity.maxAttempts;
                const attempts = activity.gradeAttempts?.length;

                if (
                    (attempts && attempts < maxAttempts) ||
                    maxAttempts === -1
                ) {
                    return true;
                }
            }
        }
    }

    // get if an activity is a composite or not
    isComposite() {
        if (this.assignment) {
            return this.assignment.shortAssignableType === assetTypeIds.BUNDLE;
        }
    }

    isCompositeActivity() {
        return this.assignment && this.assignment.compositeTaskAssignmentID;
    }

    disableResetButton(activity: CompositeTaskAssignment) {
        if (!activity.completedAt || !activity.startedAt) {
            return true;
        }
    }

    checkIfNoComposite(compositeTaskAssignment: CompositeTaskAssignment) {
        if (!compositeTaskAssignment.compositeTaskAssignmentID) {
            return true;
        }
    }

    getAssignmentName(assignment: Assignment) {
        if (assignment && assignment.asset) {
            return this.assignmentHelper.getAssetDisplayName(assignment.asset);
        }
    }

    getIPAScoringGuideUrl(assignment: Assignment, levelId?: LevelId) {
        if (assignment && assignment.asset) {
            let lId = levelId;
            if (!lId) {
                lId = assignment.asset.levelIds[0];
            }
            return this.languageHelper.getIPAScoringGuideUrl(lId);
        }
    }
    getLevelDisplayName(assignment: Assignment, levelId?: LevelId) {
        if (assignment && assignment.asset) {
            let lId = levelId;
            if (!lId) {
                lId = assignment.asset.levelIds[0];
            }
            return this.assetAssociationsHelper.getLevelDisplayName(
                this.subjects,
                assignment.asset.subjectId,
                lId
            );
        }
    }

    BundleTypeName(assignment: Assignment) {
        if (assignment && assignment.asset) {
            if (assignment.asset?.assetActivityTypeId) {
                return this.assetTypeHelper.getCapTypeName(
                    assignment.asset?.assetActivityTypeId
                );
            } else {
                return this.assetTypeHelper.getCapTypeName(
                    assignment.shortAssignableType
                );
            }
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}
